import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CustomRibbonFilters } from '@risksmart-app/domain/src/types/custom-ribbon-filter';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

export interface UpsertCustomRibbonTRPCInput {
  ParentType: Parent_Type_Enum;
  Filters: CustomRibbonFilters;
  OriginalTimestamp?: string | null;
  /** Used by the GraphQL fallback update path only (not sent to tRPC) */
  Id?: string | null;
}

export const useUpsertCustomRibbonTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.customRibbon.upsert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.customRibbon.byParentType.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification]);

  const upsertCustomRibbon = async (input: UpsertCustomRibbonTRPCInput) => {
    await mutation.mutateAsync(input);
  };

  return {
    upsertCustomRibbon,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
